import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import TableViewIcon from '@mui/icons-material/TableView';
import JsonTableRenderer from '../Common/JsonTableRenderer';

const primaryColor = '#5757b0';

const translate = (t, key, defaultValue, options = {}) => {
  if (typeof t !== 'function') {
    return defaultValue;
  }
  return t(key, { defaultValue, ...options });
};

const serializeData = (data) => {
  try {
    return JSON.stringify(data, null, 2);
  } catch (error) {
    return String(data);
  }
};

const WidgetStructuredDataRenderer = ({ data, isMaximized = false, t }) => {
  const [showRaw, setShowRaw] = React.useState(false);

  React.useEffect(() => {
    setShowRaw(false);
  }, [data]);

  const rawData = React.useMemo(() => serializeData(data), [data]);
  const tableLabels = React.useMemo(() => ({
    index: translate(t, 'widget.structured_data_index', '#'),
    key: translate(t, 'widget.structured_data_key', 'Key'),
    value: translate(t, 'widget.structured_data_value', 'Value'),
    moreItems: (count) => translate(
      t,
      'widget.structured_data_more_rows',
      `... and ${count} more rows`,
      { count }
    ),
    moreFields: (count) => translate(
      t,
      'widget.structured_data_more_fields',
      `... and ${count} more fields`,
      { count }
    )
  }), [t]);

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          mb: 0.5
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: primaryColor, fontWeight: 700, letterSpacing: 0.2 }}
        >
          {translate(t, 'widget.structured_data_title', 'Structured data')}
        </Typography>
        <Button
          type="button"
          size="small"
          variant="outlined"
          onClick={() => setShowRaw((previousValue) => !previousValue)}
          startIcon={showRaw ? <TableViewIcon /> : <CodeIcon />}
          aria-label={showRaw
            ? translate(t, 'widget.view_data_table', 'View data table')
            : translate(t, 'widget.view_raw_data', 'View raw data')}
          sx={{
            minHeight: 28,
            px: 1,
            py: 0.25,
            borderColor: `${primaryColor}60`,
            color: primaryColor,
            textTransform: 'none',
            fontSize: '0.72rem',
            '&:hover': {
              borderColor: primaryColor,
              backgroundColor: `${primaryColor}08`
            }
          }}
        >
          {showRaw
            ? translate(t, 'widget.view_data_table', 'View data table')
            : translate(t, 'widget.view_raw_data', 'View raw data')}
        </Button>
      </Box>

      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {showRaw ? (
          <Paper
            component="pre"
            elevation={0}
            sx={{
              mt: 1.5,
              mb: 0,
              p: 1.5,
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              border: `1px solid ${primaryColor}20`,
              borderRadius: 1,
              backgroundColor: '#f8f8fc',
              color: '#333',
              fontFamily: 'Consolas, Monaco, monospace',
              fontSize: '0.72rem',
              lineHeight: 1.45
            }}
          >
            {rawData}
          </Paper>
        ) : (
          <Box sx={{ flex: '1 1 auto', minHeight: 0, minWidth: 0, overflow: 'auto' }}>
            <JsonTableRenderer
              data={data}
              maxHeight={null}
              maxItems={Infinity}
              compact={false}
              disableInternalScroll
              virtualizeRows
              virtualizationThreshold={100}
              virtualizedHeight={isMaximized ? 600 : 360}
              labels={tableLabels}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(WidgetStructuredDataRenderer);
